// ============================================================
//  User Components — ExhibitorsManager Page Builder (Craft.js)
//
//  Each component:
//    1. Uses useNode() to get connect/drag refs + setProp
//    2. Declares craft.props (default values)
//    3. Declares craft.related.settings (panel component)
// ============================================================

import { useNode, useEditor, Element } from '@craftjs/core';
import React from 'react';
import { useParams } from 'react-router-dom';
import { usePublishedForm } from '../../../context/PublishedFormContext';
import type { FieldDef } from '../../../context/PublishedFormContext';
import useApiContext from '../../../hooks/useApiContext';
import { useCart } from '../../../hooks/useCart';
import { EventRepository } from '../../../services/Event/Event.repository';
import { useImageUpload } from '../hooks/useImageUpload';
import type {
  FormField,
  PricingLine,
  ValidationLine,
} from '../../../types/index';
import {
  DefaultAccueil,
  DefaultOffres,
  DefaultValidation,
  DefaultDynamicTab,
} from '../../../pages/EventEditor3/PageBuilder';
import {
  ALL_FONTS,
  FONT_WEIGHTS,
  FONT_SIZE_PRESETS,
  getTypographyStyles,
  getFontDisplayName,
  type TypographyProps,
} from '../utils/typography';
import '../styles/typography.css';

// Suggested emojis for the "Offre" block fallback image, so a cleared
// value can be reapplied with one click instead of retyping/copy-pasting.
const OFFER_EMOJI_PRESETS = ['🏢', '🏛'];

// ── Shared settings UI helpers ────────────────────────────

// Simple Rich Text Editor Component
function RichTextEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (html: string) => void;
}): React.ReactElement {
  const editorRef = React.useRef<HTMLDivElement>(null);

  // Set initial content
  React.useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const handleInput = (): void => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command: string, value: string | null = null): void => {
    // Focus editor first
    editorRef.current?.focus();

    if (command === 'insertUnorderedList') {
      insertList('ul');
    } else if (command === 'insertOrderedList') {
      insertList('ol');
    } else {
      document.execCommand(command, false, value ?? undefined);
    }

    handleInput();
  };

  const insertList = (listType: 'ul' | 'ol'): void => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    const editor = editorRef.current;

    // Focus the editor
    editor.focus();

    let selectedText = '';
    if (selection && selection.rangeCount > 0) {
      selectedText = selection.toString().trim();
    }

    // Default text if nothing is selected
    if (!selectedText) {
      selectedText = 'Nouvel élément';
    }

    // Create the list HTML
    const listHTML = `<${listType}><li>${selectedText}</li></${listType}>`;

    // Insert the list
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);

      // If text is selected, replace it
      if (!range.collapsed) {
        range.deleteContents();
      }

      // Create a temporary container
      const temp = document.createElement('div');
      temp.innerHTML = listHTML;

      // Insert the list element
      const listElement = temp.firstElementChild;
      if (listElement) {
        range.insertNode(listElement);

        // Position cursor at end of the list item
        const listItem = listElement.querySelector('li');
        if (listItem) {
          const newRange = document.createRange();
          newRange.selectNodeContents(listItem);
          newRange.collapse(false);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }
      }
    } else {
      // If no selection, append to the end
      const temp = document.createElement('div');
      temp.innerHTML = listHTML;
      const listElement = temp.firstElementChild;
      if (listElement) {
        editor.appendChild(listElement);

        // Focus the new list item
        const listItem = listElement.querySelector('li');
        if (listItem) {
          const newRange = document.createRange();
          newRange.selectNodeContents(listItem);
          newRange.collapse(false);
          if (selection) {
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        }
      }
    }
  };

  const buttonStyle = {
    padding: '4px 8px',
    margin: '0 2px',
    border: '1px solid var(--border)',
    background: 'var(--surface)',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '12px',
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    // Handle Enter key in lists
    if (e.key === 'Enter') {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const listItem = range.startContainer.parentElement?.closest('li');

        if (listItem) {
          e.preventDefault();

          // Create new list item
          const newListItem = document.createElement('li');
          newListItem.textContent = '';

          // Insert after current item
          listItem.parentNode?.insertBefore(newListItem, listItem.nextSibling);

          // Move cursor to new item
          const newRange = document.createRange();
          newRange.selectNodeContents(newListItem);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);

          handleInput();
        }
      }
    }
  };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 4 }}>
      {/* Toolbar */}
      <div
        style={{
          padding: '8px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          gap: '4px',
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          style={buttonStyle}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => execCommand('bold')}
          title="Gras"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          style={buttonStyle}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => execCommand('italic')}
          title="Italique"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          style={buttonStyle}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => execCommand('insertUnorderedList')}
          title="Liste à puces"
        >
          • Liste
        </button>
        <button
          type="button"
          style={buttonStyle}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => execCommand('insertOrderedList')}
          title="Liste numérotée"
        >
          1. Liste
        </button>
        <button
          type="button"
          style={buttonStyle}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => execCommand('removeFormat')}
          title="Supprimer le formatage"
        >
          Effacer
        </button>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        style={{
          minHeight: '120px',
          padding: '12px',
          outline: 'none',
          fontSize: '14px',
          lineHeight: '1.4',
        }}
        suppressContentEditableWarning={true}
      />
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="field-group">
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): React.ReactElement {
  return (
    <Field label={label}>
      <div className="field-color-wrap">
        <input
          type="color"
          value={value ?? 'transparent'}
          onChange={(e) => onChange(e.target.value)}
        />
        <input
          className="field-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="ex: #2563eb"
        />
      </div>
    </Field>
  );
}

// Typography Settings Component
function TypographySettings(): React.ReactElement {
  const {
    fontFamily,
    fontSize,
    fontWeight,
    textAlign,
    actions: { setProp },
  } = useNode((n) => ({
    fontFamily: n.data.props.fontFamily as string,
    fontSize: n.data.props.fontSize as string,
    fontWeight: n.data.props.fontWeight as string,
    textAlign: n.data.props.textAlign as string,
  }));

  return (
    <div className="panel-section">
      <div className="panel-section-title">Typographie</div>

      <Field label="Police">
        <select
          className="field-select"
          value={fontFamily || ''}
          onChange={(e) =>
            setProp((p: Record<string, string>) => {
              p.fontFamily = e.target.value;
            })
          }
        >
          <option value="">Défaut</option>
          <optgroup label="Google Fonts">
            {ALL_FONTS.filter((f) => f.category === 'google').map((font) => (
              <option key={font.value} value={font.value}>
                {font.name}
              </option>
            ))}
          </optgroup>
          <optgroup label="Polices système">
            {ALL_FONTS.filter((f) => f.category === 'system').map((font) => (
              <option key={font.value} value={font.value}>
                {font.name}
              </option>
            ))}
          </optgroup>
        </select>
      </Field>

      <div className="field-row">
        <Field label="Taille">
          <select
            className="field-select"
            value={fontSize || ''}
            onChange={(e) =>
              setProp((p: Record<string, string>) => {
                p.fontSize = e.target.value;
              })
            }
          >
            <option value="">Défaut</option>
            {FONT_SIZE_PRESETS.map((size) => (
              <option key={size.value} value={size.value}>
                {size.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Taille personnalisée">
          <input
            className="field-input"
            value={fontSize || ''}
            placeholder="ex: 16px, 1.2rem"
            onChange={(e) =>
              setProp((p: Record<string, string>) => {
                p.fontSize = e.target.value;
              })
            }
          />
        </Field>
      </div>

      <Field label="Épaisseur">
        <select
          className="field-select"
          value={fontWeight || ''}
          onChange={(e) =>
            setProp((p: Record<string, string>) => {
              p.fontWeight = e.target.value;
            })
          }
        >
          <option value="">Défaut</option>
          {FONT_WEIGHTS.map((weight) => (
            <option key={weight.value} value={weight.value}>
              {weight.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Alignement du texte">
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          {(['left', 'center', 'right', 'justify'] as const).map((ta) => (
            <button
              key={ta}
              className={`btn-sm ${textAlign === ta ? 'active' : ''}`}
              onClick={() =>
                setProp((p: Record<string, string>) => {
                  p.textAlign = ta;
                })
              }
            >
              {ta === 'left'
                ? '←'
                : ta === 'center'
                ? '⊞'
                : ta === 'right'
                ? '→'
                : '≡'}
            </button>
          ))}
        </div>
      </Field>

      {fontFamily && (
        <div className="field-info">
          <small>Police sélectionnée: {getFontDisplayName(fontFamily)}</small>
        </div>
      )}
    </div>
  );
}

function CommonStyleSettings(): React.ReactElement {
  const {
    backgroundColor,
    color,
    padding,
    margin,
    borderRadius,
    border,
    align,
    actions: { setProp },
  } = useNode((n) => ({
    backgroundColor: n.data.props.backgroundColor as string,
    color: n.data.props.color as string,
    padding: n.data.props.padding as string,
    margin: n.data.props.margin as string,
    borderRadius: n.data.props.borderRadius as string,
    border: n.data.props.border as string,
    align: n.data.props.align as string,
  }));

  return (
    <>
      <div className="panel-section">
        <div className="panel-section-title">Style</div>
        <ColorField
          label="Fond"
          value={backgroundColor}
          onChange={(v) =>
            setProp((p: Record<string, string>) => {
              p.backgroundColor = v;
            })
          }
        />
        <ColorField
          label="Couleur texte"
          value={color}
          onChange={(v) =>
            setProp((p: Record<string, string>) => {
              p.color = v;
            })
          }
        />
        <div className="field-row">
          <Field label="Padding">
            <input
              className="field-input"
              value={padding}
              placeholder="ex: 16px 24px"
              onChange={(e) =>
                setProp((p: Record<string, string>) => {
                  p.padding = e.target.value;
                })
              }
            />
          </Field>
          <Field label="Margin">
            <input
              className="field-input"
              value={margin}
              placeholder="ex: 8px 0"
              onChange={(e) =>
                setProp((p: Record<string, string>) => {
                  p.margin = e.target.value;
                })
              }
            />
          </Field>
        </div>
        <div className="field-row">
          <Field label="Radius">
            <input
              className="field-input"
              value={borderRadius}
              placeholder="ex: 8px"
              onChange={(e) =>
                setProp((p: Record<string, string>) => {
                  p.borderRadius = e.target.value;
                })
              }
            />
          </Field>
          <Field label="Border">
            <input
              className="field-input"
              value={border}
              placeholder="1px solid #ccc"
              onChange={(e) =>
                setProp((p: Record<string, string>) => {
                  p.border = e.target.value;
                })
              }
            />
          </Field>
        </div>
        {/* <Field label="Alignement">
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            {(['left', 'center', 'right'] as const).map((a) => (
              <button
                key={a}
                className={`btn-sm ${align === a ? 'active' : ''}`}
                onClick={() =>
                  setProp((p: Record<string, string>) => {
                    p.align = a;
                  })
                }
              >
                {a === 'left'
                  ? '← Gauche'
                  : a === 'center'
                  ? '⊞ Centre'
                  : '→ Droite'}
              </button>
            ))}
          </div>
        </Field> */}
      </div>
      <TypographySettings />
    </>
  );
}

// ── 1. TITRE ──────────────────────────────────────────────

interface TitreProps extends TypographyProps {
  text?: string;
  level?: string;
  backgroundColor?: string;
  color?: string;
  padding?: string;
  margin?: string;
  align?: string;
  borderRadius?: string;
  border?: string;
}

export const Titre = ({
  text = 'Nouveau titre',
  level = 'h2',
  backgroundColor,
  color,
  padding,
  align,
  margin,
  borderRadius,
  border,
  fontFamily,
  fontSize,
  fontWeight,
  textAlign,
}: TitreProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
  } = useNode();

  const Tag = level as keyof JSX.IntrinsicElements;
  const typographyStyles = getTypographyStyles({
    fontFamily,
    fontSize,
    fontWeight,
    textAlign,
  });

  return (
    <div
      ref={(dom) => connect(drag(dom as HTMLElement))}
      style={{
        backgroundColor: backgroundColor ?? undefined,
        color: color ?? undefined,
        padding: padding ?? undefined,
        margin: margin ?? undefined,
        borderRadius: borderRadius ?? undefined,
        border: border ?? undefined,
      }}
    >
      <div
        className="render-titre"
        style={{
          textAlign:
            textAlign ?? (align as 'left' | 'center' | 'right') ?? 'left',
          ...typographyStyles,
        }}
      >
        <Tag>{text}</Tag>
      </div>
    </div>
  );
};
Titre.craft = {
  props: {
    text: 'Nouveau titre',
    level: 'h2',
    backgroundColor: '',
    color: '',
    padding: '',
    margin: '',
    align: 'left',
    borderRadius: '',
    border: '',
    fontFamily: '',
    fontSize: '',
    fontWeight: '',
    textAlign: '',
  },
  related: {
    settings: () => {
      const {
        text,
        level,
        actions: { setProp },
      } = useNode((n) => ({
        text: n.data.props.text as string,
        level: n.data.props.level as string,
      }));
      return (
        <>
          <div className="panel-section">
            <div className="panel-section-title">Contenu</div>
            <Field label="Texte">
              <input
                className="field-input"
                value={text}
                onChange={(e) =>
                  setProp((p: { text: string }) => {
                    p.text = e.target.value;
                  })
                }
              />
            </Field>
            {/* <Field label="Niveau">
              <select
                className="field-select"
                value={level}
                onChange={(e) =>
                  setProp((p: { level: string }) => {
                    p.level = e.target.value;
                  })
                }
              >
                {['h1', 'h2', 'h3', 'h4'].map((h) => (
                  <option key={h}>{h}</option>
                ))}
              </select>
            </Field> */}
          </div>
          <CommonStyleSettings />
        </>
      );
    },
  },
};

// ── 2. TEXTE ──────────────────────────────────────────────

interface TexteProps extends TypographyProps {
  text?: string;
  backgroundColor?: string;
  color?: string;
  padding?: string;
  margin?: string;
  align?: string;
  borderRadius?: string;
  border?: string;
}

export const Texte = ({
  text = 'Votre texte ici.',
  backgroundColor,
  color,
  padding,
  margin,
  align,
  borderRadius,
  border,
  fontFamily,
  fontSize,
  fontWeight,
  textAlign,
}: TexteProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
  } = useNode();

  const typographyStyles = getTypographyStyles({
    fontFamily,
    fontSize,
    fontWeight,
    textAlign,
  });

  return (
    <div
      ref={(dom) => connect(drag(dom as HTMLElement))}
      style={{
        backgroundColor: backgroundColor ?? undefined,
        color: color ?? undefined,
        padding: padding ?? undefined,
        margin: margin ?? undefined,
        borderRadius: borderRadius ?? undefined,
        border: border ?? undefined,
      }}
    >
      <div
        className="render-texte"
        style={{
          textAlign:
            textAlign ?? (align as 'left' | 'center' | 'right') ?? 'left',
          ...typographyStyles,
        }}
        dangerouslySetInnerHTML={{ __html: text }}
      />
    </div>
  );
};
Texte.craft = {
  props: {
    text: 'Votre texte ici.',
    backgroundColor: '',
    color: '',
    padding: '',
    margin: '',
    align: 'left',
    borderRadius: '',
    border: '',
    fontFamily: '',
    fontSize: '',
    fontWeight: '',
    textAlign: '',
  },
  related: {
    settings: () => {
      const {
        text,
        actions: { setProp },
      } = useNode((n) => ({ text: n.data.props.text as string }));
      return (
        <>
          <div className="panel-section">
            <div className="panel-section-title">Contenu</div>
            <Field label="Contenu">
              <RichTextEditor
                value={text || 'Votre texte ici.'}
                onChange={(html) =>
                  setProp((p: { text: string }) => {
                    p.text = html;
                  })
                }
              />
            </Field>
          </div>
          <CommonStyleSettings />
        </>
      );
    },
  },
};

// ── 3. IMAGE ──────────────────────────────────────────────

interface ImageProps {
  src?: string;
  alt?: string;
  height?: string;
  objectFit?: string;
  backgroundColor?: string;
  color?: string;
  padding?: string;
  margin?: string;
  align?: string;
  borderRadius?: string;
  border?: string;
  fontSize?: string;
  textAlign?: string;
}

export const Image = ({
  src = '',
  alt = 'Image',
  height = '200',
  objectFit = 'cover',
  backgroundColor,
  margin,
  borderRadius,
  border,
}: ImageProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
  } = useNode();
  return (
    <div
      ref={(dom) => connect(drag(dom as HTMLElement))}
      style={{
        backgroundColor: backgroundColor ?? undefined,
        margin: margin ?? undefined,
        borderRadius: borderRadius ?? undefined,
        border: border ?? undefined,
      }}
    >
      <div className="render-image">
        {src ? (
          <img
            src={src}
            alt={alt}
            style={{
              height: `${height}px`,
              objectFit: objectFit as
                | 'cover'
                | 'contain'
                | 'fill'
                | 'scale-down'
                | 'none',
              width: '100%',
            }}
          />
        ) : (
          <div className="img-placeholder" style={{ height: `${height}px` }}>
            🖼
          </div>
        )}
      </div>
    </div>
  );
};
Image.craft = {
  props: {
    src: '',
    alt: 'Image',
    height: '200',
    objectFit: 'cover',
    backgroundColor: '',
    color: '',
    padding: '',
    margin: '',
    align: 'left',
    borderRadius: '',
    border: '',
    fontSize: '',
    textAlign: '',
  },
  related: {
    settings: () => {
      const {
        src,
        alt,
        height,
        objectFit,
        actions: { setProp },
      } = useNode((n) => ({
        src: n.data.props.src as string,
        alt: n.data.props.alt as string,
        height: n.data.props.height as string,
        objectFit: n.data.props.objectFit as string,
      }));

      const { uploadImage, isUploading, error } = useImageUpload();

      const handleImageUpload = async (file: File): Promise<void> => {
        try {
          const imageUrl = await uploadImage(file);
          setProp((p: { src: string }) => {
            p.src = imageUrl;
          });
        } catch (err) {
          console.error('Failed to upload image:', err);
        }
      };
      return (
        <>
          <div className="panel-section">
            <div className="panel-section-title">Image</div>
            <Field label="URL">
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  className="field-input"
                  style={{ flex: 1 }}
                  value={src}
                  placeholder="https://…"
                  onChange={(e) =>
                    setProp((p: { src: string }) => {
                      p.src = e.target.value;
                    })
                  }
                />
                {src && (
                  <button
                    onClick={() =>
                      setProp((p: { src: string }) => {
                        p.src = '';
                      })
                    }
                    style={{
                      width: 24,
                      height: 24,
                      border: 'none',
                      background: 'rgba(220,38,38,0.08)',
                      color: '#dc2626',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 12,
                    }}
                    title="Supprimer l'image"
                  >
                    ✕
                  </button>
                )}
              </div>
            </Field>
            <Field label="Charger un fichier">
              <input
                key={`file-image-${Date.now()}`}
                type="file"
                accept="image/*"
                style={{ fontSize: 11 }}
                disabled={isUploading}
                onChange={(e) => {
                  console.log('File input changed:', e.target.files);
                  const file = e.target.files?.[0];
                  if (!file) return;

                  console.log('File selected:', file.name, file.size);
                  handleImageUpload(file);

                  // Clear the input so same file can be selected again
                  e.target.value = '';
                }}
              />
              {isUploading && (
                <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                  Téléchargement en cours...
                </div>
              )}
              {error && (
                <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                  Erreur: {error}
                </div>
              )}
            </Field>
            {src && (
              <div style={{ marginTop: 8 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 4,
                  }}
                >
                  <small style={{ color: 'var(--muted)', fontSize: 11 }}>
                    Aperçu de l&lsquo;image:
                  </small>
                  <button
                    onClick={() =>
                      setProp((p: { src: string }) => {
                        p.src = '';
                      })
                    }
                    style={{
                      width: 20,
                      height: 20,
                      border: 'none',
                      background: 'rgba(220,38,38,0.08)',
                      color: '#dc2626',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 10,
                    }}
                    title="Supprimer l'image"
                  >
                    ✕
                  </button>
                </div>
                <img
                  src={src}
                  alt="Preview"
                  style={{
                    width: '100%',
                    height: `${height}px`,
                    objectFit: objectFit as
                      | 'cover'
                      | 'contain'
                      | 'fill'
                      | 'scale-down'
                      | 'none',
                    border: '1px solid var(--border)',
                    borderRadius: 4,
                  }}
                />
              </div>
            )}
            <Field label="Texte alt">
              <input
                className="field-input"
                value={alt}
                onChange={(e) =>
                  setProp((p: { alt: string }) => {
                    p.alt = e.target.value;
                  })
                }
              />
            </Field>
            <div className="field-row">
              <Field label="Hauteur (px)">
                <input
                  className="field-input"
                  type="number"
                  value={height}
                  onChange={(e) =>
                    setProp((p: { height: string }) => {
                      p.height = e.target.value;
                    })
                  }
                />
              </Field>
              <Field label="Mode d'affichage">
                <select
                  className="field-select"
                  value={objectFit}
                  onChange={(e) =>
                    setProp((p: { objectFit: string }) => {
                      p.objectFit = e.target.value;
                    })
                  }
                >
                  <option value="cover">Couvrir (cover)</option>
                  <option value="contain">Contenir (contain)</option>
                  <option value="fill">Étirer (fill)</option>
                  <option value="scale-down">Réduire (scale-down)</option>
                  <option value="none">Aucun (none)</option>
                </select>
              </Field>
            </div>
          </div>
        </>
      );
    },
  },
};

// ── 4. BOUTON ─────────────────────────────────────────────

interface BoutonProps extends TypographyProps {
  label?: string;
  url?: string;
  style?: string;
  backgroundColor?: string;
  color?: string;
  padding?: string;
  margin?: string;
  align?: string;
  borderRadius?: string;
  border?: string;
}

export const Bouton = ({
  label = 'Cliquer ici',
  url = '#',
  style: btnStyle = 'primary',
  align = 'left',
  backgroundColor,
  color,
  padding,
  margin,
  borderRadius,
  border,
  fontFamily,
  fontSize,
  fontWeight,
  textAlign,
}: BoutonProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
  } = useNode();

  const typographyStyles = getTypographyStyles({
    fontFamily,
    fontSize,
    fontWeight,
    textAlign,
  });

  return (
    <div
      ref={(dom) => connect(drag(dom as HTMLElement))}
      style={{
        backgroundColor: backgroundColor ?? undefined,
        color: color ?? undefined,
        padding: padding ?? undefined,
        margin: margin ?? undefined,
        borderRadius: borderRadius ?? undefined,
        border: border ?? undefined,
      }}
    >
      <div
        className={`render-bouton style-${btnStyle}`}
        style={{
          textAlign: textAlign ?? (align as 'left' | 'center' | 'right'),
          ...typographyStyles,
        }}
      >
        <a href={url || '#'}>{label}</a>
      </div>
    </div>
  );
};
Bouton.craft = {
  props: {
    label: 'Cliquer ici',
    url: '#',
    style: 'primary',
    backgroundColor: '',
    color: '',
    padding: '',
    margin: '',
    align: 'left',
    borderRadius: '',
    border: '',
    fontFamily: '',
    fontSize: '',
    fontWeight: '',
    textAlign: '',
  },
  related: {
    settings: () => {
      const {
        label,
        url,
        style: btnStyle,
        actions: { setProp },
      } = useNode((n) => ({
        label: n.data.props.label as string,
        url: n.data.props.url as string,
        style: n.data.props.style as string,
      }));
      return (
        <>
          <div className="panel-section">
            <div className="panel-section-title">Contenu</div>
            <Field label="Label">
              <input
                className="field-input"
                value={label}
                onChange={(e) =>
                  setProp((p: { label: string }) => {
                    p.label = e.target.value;
                  })
                }
              />
            </Field>
            <Field label="URL (Inclure http:// ou https://)">
              <input
                className="field-input"
                value={url}
                onChange={(e) =>
                  setProp((p: { url: string }) => {
                    p.url = e.target.value;
                  })
                }
              />
            </Field>
            <Field label="Style">
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                {(['primary', 'secondary', 'ghost'] as const).map((s) => (
                  <button
                    key={s}
                    className={`btn-sm ${btnStyle === s ? 'active' : ''}`}
                    onClick={() =>
                      setProp((p: { style: string }) => {
                        p.style = s;
                      })
                    }
                  >
                    {s}
                  </button>
                ))}
              </div>
            </Field>
          </div>
          <CommonStyleSettings />
        </>
      );
    },
  },
};

// ── 5. SÉPARATEUR ─────────────────────────────────────────

interface SepProps {
  height?: string;
}
export const Separateur = ({ height = '1' }: SepProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
  } = useNode();
  return (
    <div ref={(dom) => connect(drag(dom as HTMLElement))}>
      <div className="render-separateur">
        <hr style={{ borderTopWidth: `${height}px` }} />
      </div>
    </div>
  );
};
Separateur.craft = {
  props: { height: '1' },
  related: {
    settings: () => {
      const {
        height,
        actions: { setProp },
      } = useNode((n) => ({ height: n.data.props.height as string }));
      return (
        <div className="panel-section">
          <div className="panel-section-title">Options</div>
          <Field label="Épaisseur (px)">
            <input
              className="field-input"
              type="number"
              value={height}
              onChange={(e) =>
                setProp((p: { height: string }) => {
                  p.height = e.target.value;
                })
              }
            />
          </Field>
        </div>
      );
    },
  },
};

// ── 6. ESPACEUR ───────────────────────────────────────────

interface EspaceurProps {
  height?: string;
}
export const Espaceur = ({
  height = '32',
}: EspaceurProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
  } = useNode();
  return (
    <div
      ref={(dom) => connect(drag(dom as HTMLElement))}
      style={{ height: `${height}px` }}
    />
  );
};
Espaceur.craft = {
  props: { height: '32' },
  related: {
    settings: () => {
      const {
        height,
        actions: { setProp },
      } = useNode((n) => ({ height: n.data.props.height as string }));
      return (
        <div className="panel-section">
          <div className="panel-section-title">Options</div>
          <Field label="Hauteur (px)">
            <input
              className="field-input"
              type="number"
              value={height}
              onChange={(e) =>
                setProp((p: { height: string }) => {
                  p.height = e.target.value;
                })
              }
            />
          </Field>
        </div>
      );
    },
  },
};

// ── 7. SECTION ────────────────────────────────────────────

interface SectionProps {
  bg?: string;
  content?: string;
  backgroundColor?: string;
  color?: string;
  padding?: string;
  margin?: string;
  align?: string;
  borderRadius?: string;
  border?: string;
  fontSize?: string;
  textAlign?: string;
}
export const Section = ({
  bg = '#f8f8f8',
  content = 'Contenu de la section',
  backgroundColor,
  margin,
  borderRadius,
  border,
  fontSize,
  textAlign,
}: SectionProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
  } = useNode();
  return (
    <div
      ref={(dom) => connect(drag(dom as HTMLElement))}
      style={{
        backgroundColor: backgroundColor ?? undefined,
        margin: margin ?? undefined,
        borderRadius: borderRadius ?? undefined,
        border: border ?? undefined,
      }}
    >
      <div className="render-section">
        <div
          className="render-section-inner"
          style={{
            background: bg,
            fontSize: fontSize ?? undefined,
            textAlign:
              (textAlign as 'left' | 'center' | 'right' | 'justify') ??
              undefined,
          }}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </div>
    </div>
  );
};
Section.craft = {
  props: {
    bg: '#f8f8f8',
    content: 'Contenu de la section',
    backgroundColor: '',
    color: '',
    padding: '',
    margin: '',
    align: 'left',
    borderRadius: '',
    border: '',
    fontSize: '',
    textAlign: '',
  },
  related: {
    settings: () => {
      const {
        bg,
        content,
        actions: { setProp },
      } = useNode((n) => ({
        bg: n.data.props.bg as string,
        content: n.data.props.content as string,
      }));
      return (
        <>
          <div className="panel-section">
            <div className="panel-section-title">Contenu</div>
            <Field label="Couleur de fond">
              <input
                className="field-input"
                value={bg}
                onChange={(e) =>
                  setProp((p: { bg: string }) => {
                    p.bg = e.target.value;
                  })
                }
              />
            </Field>
            <Field label="Contenu HTML">
              <RichTextEditor
                value={content || 'Contenu de la section'}
                onChange={(html) =>
                  setProp((p: { content: string }) => {
                    p.content = html;
                  })
                }
              />
            </Field>
          </div>
        </>
      );
    },
  },
};

// ── 8. CTA ────────────────────────────────────────────────

interface CtaProps extends TypographyProps {
  title?: string;
  subtitle?: string;
  btnLabel?: string;
  btnUrl?: string;
  backgroundColor?: string;
  color?: string;
  padding?: string;
  margin?: string;
  align?: string;
  borderRadius?: string;
  border?: string;
}
export const Cta = ({
  title = "Rejoignez l'événement",
  subtitle = 'Ne manquez pas cette opportunité.',
  btnLabel = "S'inscrire",
  btnUrl = '#',
  backgroundColor,
  color,
  padding,
  margin,
  borderRadius,
  border,
  align,
  fontFamily,
  fontSize,
  fontWeight,
  textAlign,
}: CtaProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
  } = useNode();

  const typographyStyles = getTypographyStyles({
    fontFamily,
    fontSize,
    fontWeight,
    textAlign,
  });

  return (
    <div
      ref={(dom) => connect(drag(dom as HTMLElement))}
      style={{
        backgroundColor: backgroundColor ?? undefined,
        color: color ?? undefined,
        padding: padding ?? undefined,
        margin: margin ?? undefined,
        borderRadius: borderRadius ?? undefined,
        border: border ?? undefined,
      }}
    >
      <div
        className="render-cta"
        style={{
          textAlign:
            textAlign ?? (align as 'left' | 'center' | 'right') ?? 'left',
          ...typographyStyles,
        }}
      >
        <h2>{title}</h2>
        <p>{subtitle}</p>
        <a href={btnUrl || '#'}>{btnLabel}</a>
      </div>
    </div>
  );
};
Cta.craft = {
  props: {
    title: "Rejoignez l'événement",
    subtitle: 'Ne manquez pas cette opportunité.',
    btnLabel: "S'inscrire",
    btnUrl: '#',
    backgroundColor: '',
    color: '',
    padding: '',
    margin: '',
    align: 'left',
    borderRadius: '',
    border: '',
    fontFamily: '',
    fontSize: '',
    fontWeight: '',
    textAlign: '',
  },
  related: {
    settings: () => {
      const {
        title,
        subtitle,
        btnLabel,
        btnUrl,
        actions: { setProp },
      } = useNode((n) => ({
        title: n.data.props.title as string,
        subtitle: n.data.props.subtitle as string,
        btnLabel: n.data.props.btnLabel as string,
        btnUrl: n.data.props.btnUrl as string,
      }));
      return (
        <>
          <div className="panel-section">
            <div className="panel-section-title">Contenu</div>
            <Field label="Titre">
              <input
                className="field-input"
                value={title}
                onChange={(e) =>
                  setProp((p: { title: string }) => {
                    p.title = e.target.value;
                  })
                }
              />
            </Field>
            <Field label="Sous-titre">
              <input
                className="field-input"
                value={subtitle}
                onChange={(e) =>
                  setProp((p: { subtitle: string }) => {
                    p.subtitle = e.target.value;
                  })
                }
              />
            </Field>
            <Field label="Label bouton">
              <input
                className="field-input"
                value={btnLabel}
                onChange={(e) =>
                  setProp((p: { btnLabel: string }) => {
                    p.btnLabel = e.target.value;
                  })
                }
              />
            </Field>
            <Field label="URL bouton">
              <input
                className="field-input"
                value={btnUrl}
                onChange={(e) =>
                  setProp((p: { btnUrl: string }) => {
                    p.btnUrl = e.target.value;
                  })
                }
              />
            </Field>
          </div>
          <CommonStyleSettings />
        </>
      );
    },
  },
};

// ── 9. BLOC LIBRE ─────────────────────────────────────────

interface LibreProps {
  html?: string;
}
export const Libre = ({
  html = '<p style="padding:16px">Votre HTML ici</p>',
}: LibreProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
  } = useNode();
  return (
    <div ref={(dom) => connect(drag(dom as HTMLElement))}>
      <div
        className="render-libre"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
};
Libre.craft = {
  props: { html: '<p style="padding:16px">Votre HTML ici</p>' },
  related: {
    settings: () => {
      const {
        html,
        actions: { setProp },
      } = useNode((n) => ({ html: n.data.props.html as string }));
      return (
        <div className="panel-section">
          <div className="panel-section-title">HTML</div>
          <Field label="HTML personnalisé">
            <RichTextEditor
              value={html || '<p style="padding:16px">Votre HTML ici</p>'}
              onChange={(htmlContent) =>
                setProp((p: { html: string }) => {
                  p.html = htmlContent;
                })
              }
            />
          </Field>
        </div>
      );
    },
  },
};

// ── 10. FORMULAIRE ────────────────────────────────────────

interface FormulaireProps {
  title?: string;
  submitLabel?: string;
  fields?: FormField[];
  backgroundColor?: string;
  color?: string;
  padding?: string;
  margin?: string;
  align?: string;
  borderRadius?: string;
  border?: string;
  fontSize?: string;
  textAlign?: string;
}
export const Formulaire = ({
  title = 'Formulaire de contact',
  fields = [],
  padding,
  backgroundColor,
  margin,
  borderRadius,
  border,
  fontSize,
  textAlign,
}: FormulaireProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
    id,
  } = useNode((n) => ({ id: n.id }));
  const { enabled: editorEnabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));
  const {
    isPublished: contextPublished,
    registerField,
    unregisterField,
    getFieldValue,
    setFieldValue,
    getError,
  } = usePublishedForm();

  const isPublished = !editorEnabled && contextPublished;

  // Each row in `fields` is registered as its own entry in the shared,
  // localStorage-backed form context, keyed by a synthetic id scoped to
  // this node — the container has no fields of its own, only rows.
  React.useEffect(() => {
    if (!isPublished) return;
    fields.forEach((f, i) => {
      registerField({
        nodeId: `${id}__${i}`,
        label: f.label,
        type: f.type,
        required: true,
      });
    });
    return () => {
      fields.forEach((_f, i) => unregisterField(`${id}__${i}`));
    };
  }, [isPublished, id, JSON.stringify(fields)]);

  const handleCheckboxChange = (
    fieldId: string,
    option: string,
    checked: boolean
  ): void => {
    const current = (getFieldValue(fieldId) as string[]) ?? [];
    const next = checked
      ? [...current, option]
      : current.filter((v) => v !== option);
    setFieldValue(fieldId, next);
  };

  return (
    <div
      ref={(dom) => connect(drag(dom as HTMLElement))}
      style={{
        backgroundColor: backgroundColor ?? undefined,
        margin: margin ?? undefined,
        borderRadius: borderRadius ?? undefined,
        border: border ?? undefined,
      }}
    >
      <div
        className="render-formulaire"
        style={{
          padding: padding ?? undefined,
          fontSize: fontSize ?? undefined,
          textAlign:
            (textAlign as 'left' | 'center' | 'right' | 'justify') ?? undefined,
          pointerEvents: isPublished ? undefined : 'none',
        }}
      >
        {title && (
          <h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 700 }}>
            {title}
          </h3>
        )}
        {fields.map((f, i) => {
          const fieldId = `${id}__${i}`;
          const error = getError(fieldId);
          const value = getFieldValue(fieldId);
          return (
            <div className="form-group" key={i}>
              <label>{f.label}</label>
              {f.type === 'textarea' && (
                <textarea
                  rows={3}
                  placeholder={f.label}
                  readOnly={!isPublished}
                  value={(value as string) ?? ''}
                  onChange={
                    isPublished
                      ? (e) => setFieldValue(fieldId, e.target.value)
                      : undefined
                  }
                  style={error ? { borderColor: '#dc2626' } : undefined}
                />
              )}
              {f.type === 'radio' && (
                <div className="form-radio-group">
                  {(f.options ?? []).map((o, j) => (
                    <label className="form-radio-item" key={j}>
                      <input
                        type="radio"
                        name={isPublished ? `form-radio-${i}` : undefined}
                        checked={isPublished ? value === o : j === 0}
                        readOnly={!isPublished}
                        onChange={
                          isPublished
                            ? () => setFieldValue(fieldId, o)
                            : undefined
                        }
                      />{' '}
                      {o}
                    </label>
                  ))}
                </div>
              )}
              {f.type === 'checkbox' && (
                <div className="form-check-group">
                  {(f.options ?? []).map((o, j) => (
                    <label className="form-check-item" key={j}>
                      <input
                        type="checkbox"
                        checked={
                          isPublished
                            ? ((value as string[]) ?? []).includes(o)
                            : j === 0
                        }
                        readOnly={!isPublished}
                        onChange={
                          isPublished
                            ? (e) =>
                                handleCheckboxChange(
                                  fieldId,
                                  o,
                                  e.target.checked
                                )
                            : undefined
                        }
                      />{' '}
                      {o}
                    </label>
                  ))}
                </div>
              )}
              {f.type === 'select' && (
                <select
                  disabled={!isPublished}
                  value={(value as string) ?? ''}
                  onChange={
                    isPublished
                      ? (e) => setFieldValue(fieldId, e.target.value)
                      : undefined
                  }
                  style={error ? { borderColor: '#dc2626' } : undefined}
                >
                  <option value="">{f.label}</option>
                  {(f.options ?? []).map((o, j) => (
                    <option key={j} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              )}
              {!['textarea', 'radio', 'checkbox', 'select'].includes(
                f.type
              ) && (
                <input
                  type={f.type}
                  placeholder={f.label}
                  readOnly={!isPublished}
                  value={(value as string) ?? ''}
                  onChange={
                    isPublished
                      ? (e) => setFieldValue(fieldId, e.target.value)
                      : undefined
                  }
                  style={error ? { borderColor: '#dc2626' } : undefined}
                />
              )}
              {error && (
                <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>
                  {error}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const defaultFormFields: FormField[] = [
  { label: 'Nom', type: 'text' },
  { label: 'Email', type: 'email' },
  {
    label: 'Taille du stand souhaité',
    type: 'radio',
    options: ['9m²', '18m²', '36m²'],
  },
  {
    label: 'Services additionnels',
    type: 'checkbox',
    options: ['Mobilier', 'WiFi premium', 'Électricité 3kW'],
  },
  { label: 'Message', type: 'textarea' },
];

Formulaire.craft = {
  props: {
    title: 'Formulaire de contact',
    submitLabel: 'Envoyer',
    fields: defaultFormFields,
    backgroundColor: '',
    color: '',
    padding: '',
    margin: '',
    align: 'left',
    borderRadius: '',
    border: '',
    fontSize: '',
    textAlign: '',
  },
  related: {
    settings: () => {
      const {
        title,
        fields,
        actions: { setProp },
      } = useNode((n) => ({
        title: n.data.props.title as string,
        fields: (n.data.props.fields ?? []) as FormField[],
      }));
      const FIELD_TYPES = [
        { value: 'text', label: 'Texte court' },
        { value: 'email', label: 'Email' },
        { value: 'textarea', label: 'Texte long' },
        { value: 'radio', label: 'Choix unique' },
        { value: 'checkbox', label: 'Cases à cocher' },
        { value: 'select', label: 'Liste déroulante' },
      ];
      const addField = (type: string): void => {
        const defaults: Record<string, FormField> = {
          text: { label: 'Nouveau champ', type: 'text' },
          email: { label: 'Email', type: 'email' },
          textarea: { label: 'Message', type: 'textarea' },
          radio: {
            label: 'Votre choix',
            type: 'radio',
            options: ['Option A', 'Option B', 'Option C'],
          },
          checkbox: {
            label: 'Sélectionnez',
            type: 'checkbox',
            options: ['Option 1', 'Option 2'],
          },
          select: {
            label: 'Choisir',
            type: 'select',
            options: ['Option A', 'Option B'],
          },
        };
        setProp((p: { fields: FormField[] }) => {
          p.fields = [...(p.fields ?? []), defaults[type]];
        });
      };
      return (
        <>
          <div className="panel-section">
            <div className="panel-section-title">Formulaire</div>
            <Field label="Titre">
              <input
                className="field-input"
                value={title}
                onChange={(e) =>
                  setProp((p: { title: string }) => {
                    p.title = e.target.value;
                  })
                }
              />
            </Field>
          </div>
          <div className="panel-section">
            <div className="panel-section-title">Champs</div>
            {fields.map((f, i) => (
              <div
                key={i}
                style={{
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  padding: 10,
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <input
                    className="field-input"
                    style={{ flex: 1, fontSize: 12 }}
                    value={f.label}
                    onChange={(e) =>
                      setProp((p: { fields: FormField[] }) => {
                        p.fields[i] = { ...p.fields[i], label: e.target.value };
                      })
                    }
                  />
                  <button
                    onClick={() =>
                      setProp((p: { fields: FormField[] }) => {
                        p.fields = p.fields.filter((_, j) => j !== i);
                      })
                    }
                    style={{
                      width: 22,
                      height: 22,
                      border: 'none',
                      background: 'rgba(220,38,38,0.08)',
                      color: '#dc2626',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 14,
                    }}
                  >
                    ✕
                  </button>
                </div>
                <select
                  className="field-select"
                  style={{ fontSize: 11 }}
                  value={f.type}
                  onChange={(e) =>
                    setProp((p: { fields: FormField[] }) => {
                      p.fields[i] = {
                        ...p.fields[i],
                        type: e.target.value as FormField['type'],
                      };
                    })
                  }
                >
                  {FIELD_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                {['radio', 'checkbox', 'select'].includes(f.type) && (
                  <div style={{ marginTop: 6 }}>
                    <div
                      style={{
                        fontSize: 10,
                        color: 'var(--muted)',
                        fontWeight: 600,
                        marginBottom: 4,
                      }}
                    >
                      OPTIONS (une par ligne)
                    </div>
                    <textarea
                      className="field-textarea"
                      rows={3}
                      style={{ fontSize: 11 }}
                      value={(f.options ?? []).join('\n')}
                      onChange={(e) =>
                        setProp((p: { fields: FormField[] }) => {
                          p.fields[i] = {
                            ...p.fields[i],
                            options: e.target.value.split('\n'),
                          };
                        })
                      }
                    />
                  </div>
                )}
              </div>
            ))}
            <div style={{ marginTop: 8 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--muted)',
                  marginBottom: 6,
                }}
              >
                Ajouter un champ
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {[
                  ['text', 'Texte'],
                  ['email', 'Email'],
                  ['textarea', 'Long'],
                  ['radio', 'Choix'],
                  ['checkbox', 'Cases'],
                  ['select', 'Liste'],
                ].map(([t, l]) => (
                  <button
                    key={t}
                    className="btn-sm"
                    onClick={() => addField(t)}
                    style={{ fontSize: 10 }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      );
    },
  },
};

// ── 11. PRICING ───────────────────────────────────────────

interface PricingProps {
  title?: string;
  lines?: PricingLine[];
  total?: string;
  backgroundColor?: string;
  color?: string;
  padding?: string;
  margin?: string;
  align?: string;
  borderRadius?: string;
  border?: string;
  fontSize?: string;
  textAlign?: string;
}
export const Pricing = ({
  title = 'Résumé de commande',
  lines = [],
  total = '0 €',
  backgroundColor,
  padding,
  margin,
  borderRadius,
  border,
  fontSize,
  textAlign,
}: PricingProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
  } = useNode();
  return (
    <div
      ref={(dom) => connect(drag(dom as HTMLElement))}
      style={{
        backgroundColor: backgroundColor ?? undefined,
        margin: margin ?? undefined,
        borderRadius: borderRadius ?? undefined,
        border: border ?? undefined,
      }}
    >
      <div
        className="render-pricing"
        style={{
          padding: padding ?? undefined,
          fontSize: fontSize ?? undefined,
          textAlign:
            (textAlign as 'left' | 'center' | 'right' | 'justify') ?? undefined,
        }}
      >
        <div className="pricing-title">{title}</div>
        <table>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td>{l.label}</td>
                <td>{l.price}</td>
              </tr>
            ))}
            <tr className="total-row">
              <td>Total</td>
              <td>{total}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
Pricing.craft = {
  props: {
    title: 'Résumé de commande',
    lines: [
      { label: 'Stand 9m²', price: '3 600 €' },
      { label: 'Mobilier', price: '400 €' },
    ],
    total: '4 000 €',
    backgroundColor: '',
    color: '',
    padding: '',
    margin: '',
    align: 'left',
    borderRadius: '',
    border: '',
    fontSize: '',
    textAlign: '',
  },
  related: {
    settings: () => {
      const {
        title,
        lines,
        total,
        actions: { setProp },
      } = useNode((n) => ({
        title: n.data.props.title as string,
        lines: (n.data.props.lines ?? []) as PricingLine[],
        total: n.data.props.total as string,
      }));
      return (
        <div className="panel-section">
          <div className="panel-section-title">Pricing</div>
          <Field label="Titre">
            <input
              className="field-input"
              value={title}
              onChange={(e) =>
                setProp((p: { title: string }) => {
                  p.title = e.target.value;
                })
              }
            />
          </Field>
          <Field label="Total">
            <input
              className="field-input"
              value={total}
              onChange={(e) =>
                setProp((p: { total: string }) => {
                  p.total = e.target.value;
                })
              }
            />
          </Field>
          {lines.map((l, i) => (
            <div key={i} className="field-row" style={{ marginBottom: 8 }}>
              <input
                className="field-input"
                value={l.label}
                placeholder="Libellé"
                onChange={(e) =>
                  setProp((p: { lines: PricingLine[] }) => {
                    p.lines[i] = { ...p.lines[i], label: e.target.value };
                  })
                }
              />
              <input
                className="field-input"
                value={l.price}
                placeholder="Prix"
                onChange={(e) =>
                  setProp((p: { lines: PricingLine[] }) => {
                    p.lines[i] = { ...p.lines[i], price: e.target.value };
                  })
                }
              />
            </div>
          ))}
          <button
            className="btn-sm"
            onClick={() =>
              setProp((p: { lines: PricingLine[] }) => {
                p.lines = [...p.lines, { label: 'Ligne', price: '0 €' }];
              })
            }
            style={{ marginTop: 4 }}
          >
            + Ajouter une ligne
          </button>
        </div>
      );
    },
  },
};

// ── 12. OFFRE (Métier BF) ─────────────────────────────────

// Picker that loads offers defined on the current event (Offers page)
// and auto-fills the 4 fields (title, description, price, included) when
// the user selects one.
interface SavedOffer {
  id?: number | string;
  offer_id?: string | null;
  name?: string | null;
  price?: number | string | null;
  description?: string | null;
  inclusions?: string | null;
}

interface OfferPickerProps {
  // Craft.js setProp — typed loosely because the props shape is the OffreProps
  // above but TypeScript struggles with the generic from useNode.
  setProp: (cb: (p: any) => void) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
}

function OfferPicker({ setProp }: OfferPickerProps): React.ReactElement {
  const { eventId } = useParams<{ eventId: string }>();
  const { companyRepository } = useApiContext();
  const [offers, setOffers] = React.useState<SavedOffer[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [selectedId, setSelectedId] = React.useState<string>('');

  React.useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    companyRepository
      .offerByEvent(eventId)
      .then((data: SavedOffer[]) => {
        setOffers(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error('OfferPicker: failed to load offers', err);
        setOffers([]);
      })
      .finally(() => setLoading(false));
  }, [eventId, companyRepository]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const value = e.target.value;
    setSelectedId(value);
    if (!value) return;
    const picked = offers.find(
      (o) => String(o.id ?? o.offer_id ?? '') === value
    );
    if (!picked) return;

    const inclusionsList: string[] = (picked.inclusions ?? '')
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const priceValue =
      picked.price === null || picked.price === undefined
        ? ''
        : String(picked.price);

    setProp((p) => {
      p.offerId = String(picked.id ?? picked.offer_id ?? value);
      p.title = picked.name ?? '';
      p.description = picked.description ?? '';
      p.price = priceValue;
      p.included = inclusionsList;
    });
  };

  return (
    <Field label="Choisir une offre (depuis la page Offres)">
      <select
        className="field-select"
        value={selectedId}
        onChange={handleChange}
        disabled={loading || offers.length === 0}
      >
        <option value="">
          {loading
            ? 'Chargement…'
            : offers.length === 0
            ? 'Aucune offre disponible'
            : '— Sélectionner une offre —'}
        </option>
        {offers.map((o) => {
          const key = String(o.id ?? o.offer_id ?? o.name ?? Math.random());
          const price =
            o.price !== null && o.price !== undefined ? ` — ${o.price} €` : '';
          return (
            <option key={key} value={key}>
              {(o.name ?? 'Sans nom') + price}
            </option>
          );
        })}
      </select>
    </Field>
  );
}

interface OffreProps {
  offerId?: string;
  title?: string;
  badge?: string;
  description?: string;
  price?: string;
  currency?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  imageEmoji?: string;
  imageSrc?: string;
  imageHeight?: string;
  imageObjectFit?: string;
  included?: string[];
  availability?: string;
  backgroundColor?: string;
  color?: string;
  padding?: string;
  margin?: string;
  align?: string;
  borderRadius?: string;
  border?: string;
  fontSize?: string;
  textAlign?: string;
}
export const Offre = ({
  offerId = '',
  title = 'Stand 9m² — Hall A',
  badge = 'Populaire',
  description = 'Emplacement premium.',
  price = '3 600',
  currency = '€',
  ctaLabel = 'Ajouter au panier',
  ctaUrl = '#',
  imageEmoji = '🏢',
  imageSrc = '',
  imageHeight = '160',
  imageObjectFit = 'cover',
  included = [],
  availability = 'available',
  backgroundColor,
  color,
  padding,
  margin,
  borderRadius,
  border,
  align,
  fontSize,
  textAlign,
}: OffreProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
  } = useNode();
  const { hasItem, toggleItem, items, updateQuantity } = useCart();
  const cartKey = offerId || `${title}__${price}`;
  const inCart = hasItem(cartKey);
  const cartItem = items.find((i) => i.offerId === cartKey);
  const qty = cartItem?.quantity ?? 1;
  const numericPrice =
    parseFloat(
      String(price)
        .replace(/[^\d.,-]/g, '')
        .replace(',', '.')
    ) || 0;
  const handleCartClick = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    toggleItem({
      offerId: cartKey,
      title,
      price: numericPrice,
      quantity: 1,
      description,
      inclusions: included,
    });
  };
  const availClass =
    {
      available: 'avail-available',
      limited: 'avail-limited',
      sold: 'avail-sold',
    }[availability] ?? 'avail-available';
  const availLabel =
    { available: 'Disponible', limited: 'Places limitées', sold: 'Complet' }[
      availability
    ] ?? 'Disponible';
  return (
    <div
      ref={(dom) => connect(drag(dom as HTMLElement))}
      style={{
        backgroundColor: backgroundColor ?? undefined,
        color: color ?? undefined,
        padding: padding ?? undefined,
        margin: margin ?? undefined,
        borderRadius: borderRadius ?? undefined,
        border: border ?? undefined,
      }}
    >
      <div
        className="render-offre"
        style={{
          textAlign:
            (textAlign as 'left' | 'center' | 'right' | 'justify') ??
            (align as 'left' | 'center' | 'right') ??
            'left',
          fontSize: fontSize ?? undefined,
        }}
      >
        <div className={!imageEmoji ? '' : 'render-offre-img'}>
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={title}
              style={{
                width: '100%',
                height: `${imageHeight}px`,
                objectFit: imageObjectFit as
                  | 'cover'
                  | 'contain'
                  | 'fill'
                  | 'scale-down'
                  | 'none',
              }}
            />
          ) : (
            <span style={{ fontSize: 48 }}>{imageEmoji}</span>
          )}
          {badge && (imageEmoji || imageSrc) && (
            <div className="render-offre-badge">{badge}</div>
          )}
        </div>
        <div className="render-offre-body">
          <span className={`render-offre-avail ${availClass}`}>
            {availLabel}
          </span>
          <div className="render-offre-title">{title}</div>
          <div className="render-offre-desc">{description}</div>
          <div className="render-offre-price">
            {price} {currency} <span>HT</span>
          </div>
          {included.length > 0 && (
            <ul className="render-offre-incl">
              {included.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              className="render-offre-cta"
              onClick={handleCartClick}
              style={{
                background: inCart ? '#16a34a' : undefined,
                cursor: 'pointer',
                border: 'none',
                flex: 1,
              }}
            >
              {inCart ? '✓ Retirer du panier' : ctaLabel}
            </button>
            {inCart && (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  overflow: 'hidden',
                }}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (qty > 1) updateQuantity(cartKey, qty - 1);
                  }}
                  style={{
                    width: 30,
                    height: 30,
                    border: 'none',
                    background: '#f3f4f6',
                    cursor: qty > 1 ? 'pointer' : 'not-allowed',
                    fontSize: 16,
                    fontWeight: 600,
                    color: qty > 1 ? '#374151' : '#d1d5db',
                  }}
                >
                  −
                </button>
                <span
                  style={{
                    minWidth: 28,
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  {qty}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    updateQuantity(cartKey, qty + 1);
                  }}
                  style={{
                    width: 30,
                    height: 30,
                    border: 'none',
                    background: '#f3f4f6',
                    cursor: 'pointer',
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#374151',
                  }}
                >
                  +
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
Offre.craft = {
  props: {
    offerId: '',
    title: 'Stand 9m² — Hall A',
    badge: 'Populaire',
    description:
      'Emplacement premium en zone centrale. Inclut mobilier, WiFi et 2 badges.',
    price: '3 600',
    currency: '€',
    ctaLabel: 'Réserver ce stand',
    ctaUrl: '#',
    imageEmoji: '🏢',
    imageSrc: '',
    imageHeight: '160',
    imageObjectFit: 'cover',
    included: ['Mobilier de base', 'Connexion WiFi', '2 badges', 'Électricité'],
    availability: 'available',
    backgroundColor: '',
    color: '',
    padding: '',
    margin: '',
    align: 'left',
    borderRadius: '',
    border: '',
    fontSize: '',
    textAlign: '',
  },
  related: {
    settings: () => {
      const {
        title,
        badge,
        description,
        price,
        currency,
        ctaLabel,
        imageEmoji,
        imageSrc,
        imageHeight,
        imageObjectFit,
        included,
        availability,
        actions: { setProp },
      } = useNode((n) => ({
        title: n.data.props.title as string,
        badge: n.data.props.badge as string,
        description: n.data.props.description as string,
        price: n.data.props.price as string,
        currency: n.data.props.currency as string,
        ctaLabel: n.data.props.ctaLabel as string,
        imageEmoji: n.data.props.imageEmoji as string,
        imageSrc: n.data.props.imageSrc as string,
        imageHeight: n.data.props.imageHeight as string,
        imageObjectFit: n.data.props.imageObjectFit as string,
        included: (n.data.props.included ?? []) as string[],
        availability: n.data.props.availability as string,
      }));

      const { uploadImage, isUploading, error } = useImageUpload();

      const handleOfferImageUpload = async (file: File): Promise<void> => {
        try {
          const imageUrl = await uploadImage(file);
          setProp((p: { imageSrc: string }) => {
            p.imageSrc = imageUrl;
          });
        } catch (err) {
          console.error('Failed to upload offer image:', err);
        }
      };
      return (
        <>
          <div className="panel-section">
            <div className="panel-section-title">Offre</div>
            <OfferPicker setProp={setProp} />
            <Field label="Titre offre">
              <input
                className="field-input"
                value={title}
                onChange={(e) =>
                  setProp((p: { title: string }) => {
                    p.title = e.target.value;
                  })
                }
              />
            </Field>
            <Field label="Badge">
              <input
                className="field-input"
                value={badge}
                placeholder="ex: Populaire"
                onChange={(e) =>
                  setProp((p: { badge: string }) => {
                    p.badge = e.target.value;
                  })
                }
              />
            </Field>
            <Field label="Description">
              <textarea
                className="field-textarea"
                rows={3}
                value={description}
                onChange={(e) =>
                  setProp((p: { description: string }) => {
                    p.description = e.target.value;
                  })
                }
              />
            </Field>
            <div className="field-row">
              <Field label="Prix (HT)">
                <input
                  className="field-input"
                  value={price}
                  onChange={(e) =>
                    setProp((p: { price: string }) => {
                      p.price = e.target.value;
                    })
                  }
                />
              </Field>
              <Field label="Devise">
                <input
                  className="field-input"
                  value={currency}
                  onChange={(e) =>
                    setProp((p: { currency: string }) => {
                      p.currency = e.target.value;
                    })
                  }
                />
              </Field>
            </div>
            <Field label="Label CTA">
              <input
                className="field-input"
                value={ctaLabel}
                onChange={(e) =>
                  setProp((p: { ctaLabel: string }) => {
                    p.ctaLabel = e.target.value;
                  })
                }
              />
            </Field>
            <Field label="Image (URL)">
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  className="field-input"
                  style={{ flex: 1 }}
                  value={imageSrc}
                  placeholder="https://…"
                  onChange={(e) =>
                    setProp((p: { imageSrc: string }) => {
                      p.imageSrc = e.target.value;
                    })
                  }
                />
                {imageSrc && (
                  <button
                    onClick={() =>
                      setProp((p: { imageSrc: string }) => {
                        p.imageSrc = '';
                      })
                    }
                    style={{
                      width: 24,
                      height: 24,
                      border: 'none',
                      background: 'rgba(220,38,38,0.08)',
                      color: '#dc2626',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 12,
                    }}
                    title="Supprimer l'image"
                  >
                    ✕
                  </button>
                )}
              </div>
            </Field>
            <Field label="Charger un fichier">
              <input
                key={`file-offre-${Date.now()}`}
                type="file"
                accept="image/*"
                style={{ fontSize: 11 }}
                disabled={isUploading}
                onChange={(e) => {
                  console.log('Offre file input changed:', e.target.files);
                  const file = e.target.files?.[0];
                  if (!file) return;

                  console.log('Offre file selected:', file.name, file.size);
                  handleOfferImageUpload(file);

                  // Clear the input so same file can be selected again
                  e.target.value = '';
                }}
              />
              {isUploading && (
                <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                  Téléchargement en cours...
                </div>
              )}
              {error && (
                <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                  Erreur: {error}
                </div>
              )}
            </Field>
            {imageSrc && (
              <div style={{ marginTop: 8 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 4,
                  }}
                >
                  <small style={{ color: 'var(--muted)', fontSize: 11 }}>
                    Aperçu de l&lsquo;image:
                  </small>
                  <button
                    onClick={() =>
                      setProp((p: { imageSrc: string }) => {
                        p.imageSrc = '';
                      })
                    }
                    style={{
                      width: 20,
                      height: 20,
                      border: 'none',
                      background: 'rgba(220,38,38,0.08)',
                      color: '#dc2626',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 10,
                    }}
                    title="Supprimer l'image"
                  >
                    ✕
                  </button>
                </div>
                <img
                  src={imageSrc}
                  alt="Preview"
                  style={{
                    width: '100%',
                    height: `${imageHeight}px`,
                    objectFit: imageObjectFit as
                      | 'cover'
                      | 'contain'
                      | 'fill'
                      | 'scale-down'
                      | 'none',
                    border: '1px solid var(--border)',
                    borderRadius: 4,
                  }}
                />
              </div>
            )}
            <div className="field-row">
              <Field label="Hauteur image (px)">
                <input
                  className="field-input"
                  type="number"
                  value={imageHeight}
                  onChange={(e) =>
                    setProp((p: { imageHeight: string }) => {
                      p.imageHeight = e.target.value;
                    })
                  }
                />
              </Field>
              <Field label="Mode d'affichage">
                <select
                  className="field-select"
                  value={imageObjectFit}
                  onChange={(e) =>
                    setProp((p: { imageObjectFit: string }) => {
                      p.imageObjectFit = e.target.value;
                    })
                  }
                >
                  <option value="cover">Couvrir (cover)</option>
                  <option value="contain">Contenir (contain)</option>
                  <option value="fill">Étirer (fill)</option>
                  <option value="scale-down">Réduire (scale-down)</option>
                  <option value="none">Aucun (none)</option>
                </select>
              </Field>
            </div>
            <Field label="Emoji de secours">
              <input
                className="field-input"
                value={imageEmoji}
                placeholder="🏢"
                onChange={(e) =>
                  setProp((p: { imageEmoji: string }) => {
                    p.imageEmoji = e.target.value;
                  })
                }
              />
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                  marginTop: 6,
                }}
              >
                {OFFER_EMOJI_PRESETS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    className={`btn-sm ${imageEmoji === emoji ? 'active' : ''}`}
                    title="Réutiliser cet emoji"
                    onClick={() =>
                      setProp((p: { imageEmoji: string }) => {
                        p.imageEmoji = emoji;
                      })
                    }
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Inclusions (une par ligne)">
              <textarea
                className="field-textarea"
                rows={4}
                value={included.join('\n')}
                onChange={(e) =>
                  setProp((p: { included: string[] }) => {
                    p.included = e.target.value.split('\n');
                  })
                }
              />
            </Field>
            <Field label="Disponibilité">
              <select
                className="field-select"
                value={availability}
                onChange={(e) =>
                  setProp((p: { availability: string }) => {
                    p.availability = e.target.value;
                  })
                }
              >
                <option value="available">Disponible</option>
                <option value="limited">Places limitées</option>
                <option value="sold">Complet</option>
              </select>
            </Field>
          </div>
          <CommonStyleSettings />
        </>
      );
    },
  },
};

// ── 13. VALIDATION (Métier BF) ────────────────────────────

interface ValidationBlockProps {
  title?: string;
  company?: string;
  lines?: ValidationLine[];
  total?: string;
  status?: string;
  message?: string;
  submitLabel?: string;
  backgroundColor?: string;
  color?: string;
  padding?: string;
  margin?: string;
  align?: string;
  borderRadius?: string;
  border?: string;
  fontSize?: string;
  textAlign?: string;
}
export const ValidationBlock = ({
  title = 'Récapitulatif de commande',
  company = 'Acme Corp.',
  status = 'pending',
  message = '',
  submitLabel = 'Confirmer la commande',
  padding,
  margin,
  borderRadius,
  border,
  fontSize,
  textAlign,
}: ValidationBlockProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
  } = useNode();
  const { items, total, removeItem, updateQuantity, clear } = useCart();
  const {
    validate: validateFormFields,
    getFormJson,
    clearStorage: clearFormStorage,
  } = usePublishedForm();

  const [contactFirstName, setContactFirstName] = React.useState('');
  const [contactLastName, setContactLastName] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [contactPhone, setContactPhone] = React.useState('');
  const [contactFunction, setContactFunction] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [orderSuccess, setOrderSuccess] = React.useState<{
    id: number;
    signingUrl?: string | null;
    message?: string | null;
  } | null>(null);
  const [acceptCgv, setAcceptCgv] = React.useState(false);
  const validationTopRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (orderSuccess ?? submitError) {
      validationTopRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [orderSuccess, submitError]);

  const [siretQuery, setSiretQuery] = React.useState('');
  const [siretLoading, setSiretLoading] = React.useState(false);
  const [siretError, setSiretError] = React.useState<string | null>(null);
  const [companyInfo, setCompanyInfo] = React.useState<{
    name?: string;
    siret?: string;
    siren?: string;
    address?: string;
    city?: string;
    postal_code?: string;
    country?: string;
  } | null>(null);

  const getPublishedSlug = (): { basePath: string; slug: string } | null => {
    const parts = window.location.pathname.split('/').filter(Boolean);
    const idx = parts.indexOf('published');
    if (idx >= 0 && parts[idx + 1] && parts[idx - 1]) {
      return { basePath: parts[idx - 1], slug: parts[idx + 1] };
    }
    return null;
  };

  const handleSubmitOrder = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    if (!acceptCgv) return;
    if (items.length === 0) {
      setSubmitError(
        'Votre panier est vide. Ajoutez au moins une offre avant de valider.'
      );
      return;
    }
    const ctx = getPublishedSlug();
    if (!ctx) {
      setSubmitError(
        'La commande ne peut être envoyée que depuis la page publiée.'
      );
      return;
    }
    if (!contactEmail.trim() || !contactLastName.trim()) {
      setSubmitError('Nom et email du contact sont obligatoires.');
      return;
    }
    if (!validateFormFields()) {
      setSubmitError(
        'Veuillez remplir tous les champs obligatoires du formulaire.'
      );
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const repo = new EventRepository();
      const typedSiret = siretQuery.replace(/\s+/g, '');
      const res = await repo.createPublicOrder(ctx.slug, {
        company_name: companyInfo?.name ?? company,
        siret: companyInfo?.siret ?? (typedSiret || null),
        siren: companyInfo?.siren ?? null,
        address: companyInfo?.address ?? null,
        postal_code: companyInfo?.postal_code ?? null,
        city: companyInfo?.city ?? null,
        country: companyInfo?.country ?? null,
        contact_first_name: contactFirstName,
        contact_last_name: contactLastName,
        contact_email: contactEmail,
        contact_phone: contactPhone,
        contact_function: contactFunction,
        items,
        cgv_accepted: true,
        form_json: getFormJson(),
      });
      setOrderSuccess({
        id: res?.order?.id,
        signingUrl: res?.signing_url ?? null,
        message: res?.message ?? null,
      });
      clear();
      clearFormStorage();
    } catch (err: any) {
      setSubmitError(err?.message || 'Erreur lors de la commande');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSiretSearch = async (
    e?: React.MouseEvent | React.FormEvent
  ): Promise<void> => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const cleaned = siretQuery.replace(/\s+/g, '');
    if (cleaned.length < 9) {
      setSiretError('SIRET ou SIREN invalide (9 ou 14 chiffres)');
      return;
    }
    setSiretLoading(true);
    setSiretError(null);
    try {
      const repo = new EventRepository();
      const data = await repo.publicSiretLookup(cleaned);
      setCompanyInfo(data);
    } catch (err: any) {
      setSiretError(err?.message || 'Entreprise introuvable');
      setCompanyInfo(null);
    } finally {
      setSiretLoading(false);
    }
  };

  const stClass =
    {
      pending: 'status-pending',
      validated: 'status-validated',
      rejected: 'status-rejected',
    }[status] ?? 'status-pending';
  const stLabel =
    { pending: 'En attente', validated: 'Validé', rejected: 'Refusé' }[
      status
    ] ?? 'En attente';

  const formatPrice = (n: number): string =>
    new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(n);

  const submitDisabled = !acceptCgv || submitting;

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 13,
    outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 4,
    color: '#374151',
  };

  return (
    <div ref={(dom) => connect(drag(dom as HTMLElement))}>
      <div
        ref={validationTopRef}
        className="render-validation"
        style={{
          padding: padding ?? undefined,
          margin: margin ?? undefined,
          borderRadius: borderRadius ?? undefined,
          border: border ?? undefined,
          fontSize: fontSize ?? undefined,
          textAlign:
            (textAlign as 'left' | 'center' | 'right' | 'justify') ?? undefined,
        }}
      >
        <div className="val-title">{title}</div>

        {orderSuccess && (
          <div
            style={{
              margin: '16px 0',
              padding: '14px 18px',
              background: '#ecfdf5',
              border: '1px solid #6ee7b7',
              borderRadius: 8,
              color: '#065f46',
              fontSize: 14,
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            <div>
              {orderSuccess.message ??
                `✓ Votre demande #${orderSuccess.id} a été envoyée.`}
            </div>
            {orderSuccess.signingUrl && (
              <a
                href={orderSuccess.signingUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  marginTop: 12,
                  padding: '10px 24px',
                  background: '#059669',
                  color: '#fff',
                  borderRadius: 6,
                  textDecoration: 'none',
                  fontSize: 15,
                  fontWeight: 700,
                }}
              >
                Signer le contrat
              </a>
            )}
          </div>
        )}
        {submitError && (
          <div
            style={{
              margin: '16px 0',
              padding: '12px 16px',
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: 8,
              color: '#b91c1c',
              fontSize: 13,
            }}
          >
            {submitError}
          </div>
        )}

        {/* SIRET lookup */}
        <div style={{ margin: '16px 0 24px' }}>
          <label style={{ ...labelStyle, fontSize: 13, marginBottom: 8 }}>
            SIRET<span style={{ color: '#dc2626' }}>*</span>
            <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 6 }}>
              (recherche réservée aux entreprises françaises, pré-remplit
              certains champs)
            </span>
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={siretQuery}
              onChange={(e) => setSiretQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSiretSearch().catch(() => undefined);
                }
              }}
              placeholder="14 chiffres"
              style={{ ...inputStyle, flex: '0 0 220px', padding: '10px 12px' }}
            />
            <button
              type="button"
              onClick={(e) => {
                handleSiretSearch(e).catch(() => undefined);
              }}
              disabled={siretLoading || !siretQuery.trim()}
              style={{
                padding: '10px 28px',
                background: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 14,
                cursor:
                  siretLoading || !siretQuery.trim()
                    ? 'not-allowed'
                    : 'pointer',
                opacity: siretLoading || !siretQuery.trim() ? 0.6 : 1,
              }}
            >
              {siretLoading ? 'Recherche…' : 'Chercher'}
            </button>
          </div>
          {siretError && (
            <div
              style={{
                marginTop: 8,
                padding: '8px 12px',
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                color: '#b91c1c',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {siretError}
            </div>
          )}
          {companyInfo && (
            <div
              style={{
                marginTop: 12,
                padding: '12px 14px',
                background: '#f0fdf4',
                border: '1px solid #86efac',
                borderRadius: 6,
                fontSize: 13,
                color: '#14532d',
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                {companyInfo.name ?? '—'}
              </div>
              <div>SIRET : {companyInfo.siret}</div>
              {companyInfo.address && (
                <div>
                  {companyInfo.address}
                  {companyInfo.postal_code
                    ? `, ${companyInfo.postal_code}`
                    : ''}
                  {companyInfo.city ? ` ${companyInfo.city}` : ''}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="val-row">
          <span className="val-label">Société</span>
          <span>{companyInfo?.name ?? company}</span>
        </div>

        {/* Contact fields */}
        <div
          style={{
            margin: '20px 0',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 12,
          }}
        >
          <div>
            <label style={labelStyle}>
              Nom<span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="text"
              value={contactLastName}
              onChange={(e) => setContactLastName(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Prénom</label>
            <input
              type="text"
              value={contactFirstName}
              onChange={(e) => setContactFirstName(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>
              Email<span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Téléphone</label>
            <input
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Fonction</label>
            <input
              type="text"
              value={contactFunction}
              onChange={(e) => setContactFunction(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        {items.length === 0 ? (
          <div
            style={{
              padding: '24px 0',
              color: '#6b7280',
              fontStyle: 'italic',
              textAlign: 'center',
            }}
          >
            Votre panier est vide. Retournez à la page Offres pour ajouter des
            stands.
          </div>
        ) : (
          <>
            {items.map((item) => (
              <div className="val-row" key={item.offerId}>
                <span className="val-label">{item.title}</span>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 2,
                      border: '1px solid #d1d5db',
                      borderRadius: 6,
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if ((item.quantity ?? 1) > 1) {
                          updateQuantity(
                            item.offerId,
                            (item.quantity ?? 1) - 1
                          );
                        }
                      }}
                      style={{
                        width: 26,
                        height: 26,
                        border: 'none',
                        background: '#f3f4f6',
                        cursor:
                          (item.quantity ?? 1) > 1 ? 'pointer' : 'not-allowed',
                        fontSize: 14,
                        fontWeight: 600,
                        color: (item.quantity ?? 1) > 1 ? '#374151' : '#d1d5db',
                      }}
                    >
                      −
                    </button>
                    <span
                      style={{
                        minWidth: 24,
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      {item.quantity ?? 1}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        updateQuantity(item.offerId, (item.quantity ?? 1) + 1);
                      }}
                      style={{
                        width: 26,
                        height: 26,
                        border: 'none',
                        background: '#f3f4f6',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#374151',
                      }}
                    >
                      +
                    </button>
                  </div>
                  {formatPrice(item.price * (item.quantity ?? 1))}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeItem(item.offerId);
                    }}
                    title="Retirer"
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: '#b91c1c',
                      cursor: 'pointer',
                      fontSize: 16,
                      padding: 0,
                    }}
                  >
                    ✕
                  </button>
                </span>
              </div>
            ))}
            <div className="val-total">{formatPrice(total)}</div>
          </>
        )}

        {/* CGV acceptance */}
        <div style={{ marginTop: 20, display: 'grid', gap: 10 }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              fontSize: 13,
              color: '#374151',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={acceptCgv}
              onChange={(e) => setAcceptCgv(e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <span>
              Je reconnais avoir lu et accepté{' '}
              <a
                href="https://business-france-v2.cdn.prismic.io/business-france-v2/aOTjq55xUNkB1rHW_Conditionsg%C3%A9n%C3%A9ralesdevente_Septembre2025_FR.pdf"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: '#2563eb', textDecoration: 'underline' }}
              >
                les conditions générales de vente
              </a>{' '}
              et m&apos;engage à les respecter sans réserve
            </span>
          </label>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 10,
            marginTop: 16,
            justifyContent: 'flex-end',
          }}
        >
          {items.length > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clear();
              }}
              style={{
                padding: '10px 18px',
                background: '#fff',
                color: '#b91c1c',
                border: '1px solid #fca5a5',
                borderRadius: 8,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Vider le panier
            </button>
          )}
          <button
            type="button"
            className="val-submit"
            onClick={(e) => {
              handleSubmitOrder(e).catch(() => undefined);
            }}
            disabled={submitDisabled}
            title={
              !acceptCgv
                ? 'Veuillez accepter les conditions générales de vente'
                : undefined
            }
            style={{
              opacity: submitDisabled ? 0.5 : 1,
              cursor: submitDisabled ? 'not-allowed' : 'pointer',
              border: 'none',
            }}
          >
            {submitting ? 'Envoi…' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
ValidationBlock.craft = {
  props: {
    title: 'Récapitulatif de commande',
    company: 'Acme Corp.',
    lines: [
      { label: 'Stand 9m²', val: '3 600 €' },
      { label: 'Options', val: '400 €' },
    ],
    total: '4 000 €',
    status: 'pending',
    message: "Votre commande est en cours de validation par l'équipe projet.",
    submitLabel: 'Confirmer la commande',
    backgroundColor: '',
    color: '',
    padding: '',
    margin: '',
    align: 'left',
    borderRadius: '',
    border: '',
    fontSize: '',
    textAlign: '',
  },
  related: {
    settings: () => {
      const {
        title,
        company,
        total,
        status,
        message,
        submitLabel,
        actions: { setProp },
      } = useNode((n) => ({
        title: n.data.props.title as string,
        company: n.data.props.company as string,
        total: n.data.props.total as string,
        status: n.data.props.status as string,
        message: n.data.props.message as string,
        submitLabel: n.data.props.submitLabel as string,
      }));
      return (
        <div className="panel-section">
          <div className="panel-section-title">Validation</div>
          <Field label="Titre">
            <input
              className="field-input"
              value={title}
              onChange={(e) =>
                setProp((p: { title: string }) => {
                  p.title = e.target.value;
                })
              }
            />
          </Field>
          <Field label="Société">
            <input
              className="field-input"
              value={company}
              onChange={(e) =>
                setProp((p: { company: string }) => {
                  p.company = e.target.value;
                })
              }
            />
          </Field>
          <Field label="Total">
            <input
              className="field-input"
              value={total}
              onChange={(e) =>
                setProp((p: { total: string }) => {
                  p.total = e.target.value;
                })
              }
            />
          </Field>
          <Field label="Statut">
            <select
              className="field-select"
              value={status}
              onChange={(e) =>
                setProp((p: { status: string }) => {
                  p.status = e.target.value;
                })
              }
            >
              <option value="pending">En attente</option>
              <option value="validated">Validé</option>
              <option value="rejected">Refusé</option>
            </select>
          </Field>
          <Field label="Message organisateur">
            <textarea
              className="field-textarea"
              rows={3}
              value={message}
              onChange={(e) =>
                setProp((p: { message: string }) => {
                  p.message = e.target.value;
                })
              }
            />
          </Field>
          <Field label="Label bouton">
            <input
              className="field-input"
              value={submitLabel}
              onChange={(e) =>
                setProp((p: { submitLabel: string }) => {
                  p.submitLabel = e.target.value;
                })
              }
            />
          </Field>
        </div>
      );
    },
  },
};

// ── 14. FORM FIELD — Text Input ───────────────────────────

interface FormFieldInputProps {
  label?: string;
  placeholder?: string;
  required?: boolean;
  defaultValue?: string;
}

export const FormFieldInput = ({
  label = 'Champ texte',
  placeholder = 'Saisissez votre texte...',
  required = false,
  defaultValue = '',
}: FormFieldInputProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
    id,
  } = useNode((n) => ({ id: n.id }));
  const {
    isPublished,
    registerField,
    unregisterField,
    getFieldValue,
    setFieldValue,
    getError,
  } = usePublishedForm();

  React.useEffect(() => {
    if (!isPublished) return;
    registerField({ nodeId: id, label, type: 'text', required });
    return () => unregisterField(id);
  }, [isPublished, id, label, required, registerField, unregisterField]);

  const error = getError(id);

  return (
    <div ref={(dom) => connect(drag(dom as HTMLElement))}>
      <div style={{ padding: '8px 16px' }}>
        <label
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 6,
            color: '#374151',
          }}
        >
          {label}
          {required && (
            <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>
          )}
        </label>
        {isPublished ? (
          <input
            type="text"
            placeholder={placeholder}
            value={getFieldValue(id) as string}
            onChange={(e) => setFieldValue(id, e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${error ? '#dc2626' : '#d1d5db'}`,
              borderRadius: 6,
              fontSize: 14,
              color: '#374151',
              background: '#f9fafb',
              boxSizing: 'border-box',
            }}
          />
        ) : (
          <input
            type="text"
            placeholder={placeholder}
            defaultValue={defaultValue}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14,
              color: '#374151',
              background: '#f9fafb',
              boxSizing: 'border-box',
            }}
          />
        )}
        {error && (
          <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};
FormFieldInput.craft = {
  displayName: 'FormFieldInput',
  props: {
    label: 'Champ texte',
    placeholder: 'Saisissez votre texte...',
    required: false,
    defaultValue: '',
  },
  related: {
    settings: () => {
      const {
        label,
        placeholder,
        required,
        defaultValue,
        actions: { setProp },
      } = useNode((n) => ({
        label: n.data.props.label as string,
        placeholder: n.data.props.placeholder as string,
        required: n.data.props.required as boolean,
        defaultValue: n.data.props.defaultValue as string,
      }));
      return (
        <div className="panel-section">
          <div className="panel-section-title">Champ texte</div>
          <Field label="Label">
            <input
              className="field-input"
              value={label}
              onChange={(e) =>
                setProp((p: FormFieldInputProps) => {
                  p.label = e.target.value;
                })
              }
            />
          </Field>
          <Field label="Placeholder">
            <input
              className="field-input"
              value={placeholder}
              onChange={(e) =>
                setProp((p: FormFieldInputProps) => {
                  p.placeholder = e.target.value;
                })
              }
            />
          </Field>
          <Field label="Valeur par défaut">
            <input
              className="field-input"
              value={defaultValue}
              onChange={(e) =>
                setProp((p: FormFieldInputProps) => {
                  p.defaultValue = e.target.value;
                })
              }
            />
          </Field>
          <div className="toggle-row">
            <span className="toggle-label">Obligatoire</span>
            <label className="toggle">
              <input
                type="checkbox"
                checked={required}
                onChange={() =>
                  setProp((p: FormFieldInputProps) => {
                    p.required = !p.required;
                  })
                }
              />
              <span className="toggle-slider" />
            </label>
          </div>
        </div>
      );
    },
  },
};

// ── 15. FORM FIELD — Text Area ────────────────────────────

interface FormFieldTextareaProps {
  label?: string;
  placeholder?: string;
  required?: boolean;
  defaultValue?: string;
}

export const FormFieldTextarea = ({
  label = 'Zone de texte',
  placeholder = 'Saisissez votre message...',
  required = false,
  defaultValue = '',
}: FormFieldTextareaProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
    id,
  } = useNode((n) => ({ id: n.id }));
  const {
    isPublished,
    registerField,
    unregisterField,
    getFieldValue,
    setFieldValue,
    getError,
  } = usePublishedForm();

  React.useEffect(() => {
    if (!isPublished) return;
    registerField({ nodeId: id, label, type: 'textarea', required });
    return () => unregisterField(id);
  }, [isPublished, id, label, required, registerField, unregisterField]);

  const error = getError(id);

  return (
    <div ref={(dom) => connect(drag(dom as HTMLElement))}>
      <div style={{ padding: '8px 16px' }}>
        <label
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 6,
            color: '#374151',
          }}
        >
          {label}
          {required && (
            <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>
          )}
        </label>
        {isPublished ? (
          <textarea
            placeholder={placeholder}
            value={getFieldValue(id) as string}
            onChange={(e) => setFieldValue(id, e.target.value)}
            rows={3}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${error ? '#dc2626' : '#d1d5db'}`,
              borderRadius: 6,
              fontSize: 14,
              color: '#374151',
              background: '#f9fafb',
              boxSizing: 'border-box',
              resize: 'none',
            }}
          />
        ) : (
          <textarea
            placeholder={placeholder}
            defaultValue={defaultValue}
            rows={3}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14,
              color: '#374151',
              background: '#f9fafb',
              boxSizing: 'border-box',
              resize: 'none',
            }}
          />
        )}
        {error && (
          <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};
FormFieldTextarea.craft = {
  displayName: 'FormFieldTextarea',
  props: {
    label: 'Zone de texte',
    placeholder: 'Saisissez votre message...',
    required: false,
    defaultValue: '',
  },
  related: {
    settings: () => {
      const {
        label,
        placeholder,
        required,
        defaultValue,
        actions: { setProp },
      } = useNode((n) => ({
        label: n.data.props.label as string,
        placeholder: n.data.props.placeholder as string,
        required: n.data.props.required as boolean,
        defaultValue: n.data.props.defaultValue as string,
      }));
      return (
        <div className="panel-section">
          <div className="panel-section-title">Zone de texte</div>
          <Field label="Label">
            <input
              className="field-input"
              value={label}
              onChange={(e) =>
                setProp((p: FormFieldTextareaProps) => {
                  p.label = e.target.value;
                })
              }
            />
          </Field>
          <Field label="Placeholder">
            <input
              className="field-input"
              value={placeholder}
              onChange={(e) =>
                setProp((p: FormFieldTextareaProps) => {
                  p.placeholder = e.target.value;
                })
              }
            />
          </Field>
          <Field label="Valeur par défaut">
            <input
              className="field-input"
              value={defaultValue}
              onChange={(e) =>
                setProp((p: FormFieldTextareaProps) => {
                  p.defaultValue = e.target.value;
                })
              }
            />
          </Field>
          <div className="toggle-row">
            <span className="toggle-label">Obligatoire</span>
            <label className="toggle">
              <input
                type="checkbox"
                checked={required}
                onChange={() =>
                  setProp((p: FormFieldTextareaProps) => {
                    p.required = !p.required;
                  })
                }
              />
              <span className="toggle-slider" />
            </label>
          </div>
        </div>
      );
    },
  },
};

// ── 16. FORM FIELD — Radio Button ─────────────────────────

interface FormFieldRadioProps {
  label?: string;
  required?: boolean;
  options?: string[];
}

export const FormFieldRadio = ({
  label = 'Bouton radio',
  required = false,
  options = ['Option 1', 'Option 2', 'Option 3'],
}: FormFieldRadioProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
    id,
  } = useNode((n) => ({ id: n.id }));
  const {
    isPublished,
    registerField,
    unregisterField,
    getFieldValue,
    setFieldValue,
    getError,
  } = usePublishedForm();

  React.useEffect(() => {
    if (!isPublished) return;
    registerField({ nodeId: id, label, type: 'radio', required });
    return () => unregisterField(id);
  }, [isPublished, id, label, required, registerField, unregisterField]);

  const error = getError(id);
  const selectedValue = getFieldValue(id) as string;

  return (
    <div ref={(dom) => connect(drag(dom as HTMLElement))}>
      <div style={{ padding: '8px 16px' }}>
        <label
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 8,
            color: '#374151',
          }}
        >
          {label}
          {required && (
            <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>
          )}
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {options.map((opt, i) => (
            <label
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                color: '#374151',
                cursor: isPublished ? 'pointer' : 'default',
              }}
            >
              <input
                type="radio"
                name={id}
                checked={isPublished ? selectedValue === opt : undefined}
                onChange={
                  isPublished ? () => setFieldValue(id, opt) : undefined
                }
                readOnly={!isPublished}
              />
              {opt}
            </label>
          ))}
        </div>
        {error && (
          <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};
FormFieldRadio.craft = {
  displayName: 'FormFieldRadio',
  props: {
    label: 'Bouton radio',
    required: false,
    options: ['Option 1', 'Option 2', 'Option 3'],
  },
  related: {
    settings: () => {
      const {
        label,
        required,
        options,
        actions: { setProp },
      } = useNode((n) => ({
        label: n.data.props.label as string,
        required: n.data.props.required as boolean,
        options: (n.data.props.options ?? []) as string[],
      }));
      return (
        <div className="panel-section">
          <div className="panel-section-title">Bouton radio</div>
          <Field label="Label">
            <input
              className="field-input"
              value={label}
              onChange={(e) =>
                setProp((p: FormFieldRadioProps) => {
                  p.label = e.target.value;
                })
              }
            />
          </Field>
          <div className="toggle-row">
            <span className="toggle-label">Obligatoire</span>
            <label className="toggle">
              <input
                type="checkbox"
                checked={required}
                onChange={() =>
                  setProp((p: FormFieldRadioProps) => {
                    p.required = !p.required;
                  })
                }
              />
              <span className="toggle-slider" />
            </label>
          </div>
          <div style={{ marginTop: 10 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: 6,
              }}
            >
              Options
            </div>
            {options.map((opt, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 6,
                  alignItems: 'center',
                  marginBottom: 4,
                }}
              >
                <input
                  className="field-input"
                  style={{ flex: 1, fontSize: 12 }}
                  value={opt}
                  onChange={(e) =>
                    setProp((p: FormFieldRadioProps) => {
                      const next = [...(p.options ?? [])];
                      next[i] = e.target.value;
                      p.options = next;
                    })
                  }
                />
                <button
                  onClick={() =>
                    setProp((p: FormFieldRadioProps) => {
                      p.options = (p.options ?? []).filter((_, j) => j !== i);
                    })
                  }
                  style={{
                    width: 22,
                    height: 22,
                    border: 'none',
                    background: 'rgba(220,38,38,0.08)',
                    color: '#dc2626',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: 14,
                    flexShrink: 0,
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              className="btn-sm"
              onClick={() =>
                setProp((p: FormFieldRadioProps) => {
                  p.options = [
                    ...(p.options ?? []),
                    `Option ${(p.options ?? []).length + 1}`,
                  ];
                })
              }
              style={{ marginTop: 4, fontSize: 11 }}
            >
              + Ajouter une option
            </button>
          </div>
        </div>
      );
    },
  },
};

// ── 17. FORM FIELD — Checkbox ─────────────────────────────

interface FormFieldCheckboxProps {
  label?: string;
  required?: boolean;
  options?: string[];
}

export const FormFieldCheckbox = ({
  label = 'Case à cocher',
  required = false,
  options = ['Option 1', 'Option 2'],
}: FormFieldCheckboxProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
    id,
  } = useNode((n) => ({ id: n.id }));
  const {
    isPublished,
    registerField,
    unregisterField,
    getFieldValue,
    setFieldValue,
    getError,
  } = usePublishedForm();

  React.useEffect(() => {
    if (!isPublished) return;
    registerField({ nodeId: id, label, type: 'checkbox', required });
    return () => unregisterField(id);
  }, [isPublished, id, label, required, registerField, unregisterField]);

  const error = getError(id);
  const selectedOptions = (getFieldValue(id) as string[]) ?? [];

  const handleCheckboxChange = (opt: string, checked: boolean): void => {
    const next = checked
      ? [...selectedOptions, opt]
      : selectedOptions.filter((v) => v !== opt);
    setFieldValue(id, next);
  };

  return (
    <div ref={(dom) => connect(drag(dom as HTMLElement))}>
      <div style={{ padding: '8px 16px' }}>
        <label
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 8,
            color: '#374151',
          }}
        >
          {label}
          {required && (
            <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>
          )}
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {options.map((opt, i) => (
            <label
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                color: '#374151',
                cursor: isPublished ? 'pointer' : 'default',
              }}
            >
              <input
                type="checkbox"
                checked={
                  isPublished ? selectedOptions.includes(opt) : undefined
                }
                onChange={
                  isPublished
                    ? (e) => handleCheckboxChange(opt, e.target.checked)
                    : undefined
                }
                readOnly={!isPublished}
              />
              {opt}
            </label>
          ))}
        </div>
        {error && (
          <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};
FormFieldCheckbox.craft = {
  displayName: 'FormFieldCheckbox',
  props: {
    label: 'Case à cocher',
    required: false,
    options: ['Option 1', 'Option 2'],
  },
  related: {
    settings: () => {
      const {
        label,
        required,
        options,
        actions: { setProp },
      } = useNode((n) => ({
        label: n.data.props.label as string,
        required: n.data.props.required as boolean,
        options: (n.data.props.options ?? []) as string[],
      }));
      return (
        <div className="panel-section">
          <div className="panel-section-title">Case à cocher</div>
          <Field label="Label">
            <input
              className="field-input"
              value={label}
              onChange={(e) =>
                setProp((p: FormFieldCheckboxProps) => {
                  p.label = e.target.value;
                })
              }
            />
          </Field>
          <div className="toggle-row">
            <span className="toggle-label">Obligatoire</span>
            <label className="toggle">
              <input
                type="checkbox"
                checked={required}
                onChange={() =>
                  setProp((p: FormFieldCheckboxProps) => {
                    p.required = !p.required;
                  })
                }
              />
              <span className="toggle-slider" />
            </label>
          </div>
          <div style={{ marginTop: 10 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: 6,
              }}
            >
              Options
            </div>
            {options.map((opt, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 6,
                  alignItems: 'center',
                  marginBottom: 4,
                }}
              >
                <input
                  className="field-input"
                  style={{ flex: 1, fontSize: 12 }}
                  value={opt}
                  onChange={(e) =>
                    setProp((p: FormFieldCheckboxProps) => {
                      const next = [...(p.options ?? [])];
                      next[i] = e.target.value;
                      p.options = next;
                    })
                  }
                />
                <button
                  onClick={() =>
                    setProp((p: FormFieldCheckboxProps) => {
                      p.options = (p.options ?? []).filter((_, j) => j !== i);
                    })
                  }
                  style={{
                    width: 22,
                    height: 22,
                    border: 'none',
                    background: 'rgba(220,38,38,0.08)',
                    color: '#dc2626',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: 14,
                    flexShrink: 0,
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              className="btn-sm"
              onClick={() =>
                setProp((p: FormFieldCheckboxProps) => {
                  p.options = [
                    ...(p.options ?? []),
                    `Option ${(p.options ?? []).length + 1}`,
                  ];
                })
              }
              style={{ marginTop: 4, fontSize: 11 }}
            >
              + Ajouter une option
            </button>
          </div>
        </div>
      );
    },
  },
};

// ── 18. FORM FIELD — Select Dropdown ──────────────────────

interface FormFieldSelectProps {
  label?: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];
}

export const FormFieldSelect = ({
  label = 'Liste déroulante',
  placeholder = '— Sélectionner —',
  required = false,
  options = ['Option A', 'Option B', 'Option C'],
}: FormFieldSelectProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
    id,
  } = useNode((n) => ({ id: n.id }));
  const {
    isPublished,
    registerField,
    unregisterField,
    getFieldValue,
    setFieldValue,
    getError,
  } = usePublishedForm();

  React.useEffect(() => {
    if (!isPublished) return;
    registerField({ nodeId: id, label, type: 'select', required });
    return () => unregisterField(id);
  }, [isPublished, id, label, required, registerField, unregisterField]);

  const error = getError(id);

  return (
    <div ref={(dom) => connect(drag(dom as HTMLElement))}>
      <div style={{ padding: '8px 16px' }}>
        <label
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 6,
            color: '#374151',
          }}
        >
          {label}
          {required && (
            <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>
          )}
        </label>
        {isPublished ? (
          <select
            value={getFieldValue(id) as string}
            onChange={(e) => setFieldValue(id, e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${error ? '#dc2626' : '#d1d5db'}`,
              borderRadius: 6,
              fontSize: 14,
              color: '#374151',
              background: '#f9fafb',
              boxSizing: 'border-box',
            }}
          >
            <option value="">{placeholder}</option>
            {options.map((opt, i) => (
              <option key={i} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        ) : (
          <select
            defaultValue=""
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14,
              color: '#374151',
              background: '#f9fafb',
              boxSizing: 'border-box',
            }}
          >
            <option value="">{placeholder}</option>
            {options.map((opt, i) => (
              <option key={i} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        )}
        {error && (
          <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};
FormFieldSelect.craft = {
  displayName: 'FormFieldSelect',
  props: {
    label: 'Liste déroulante',
    placeholder: '— Sélectionner —',
    required: false,
    options: ['Option A', 'Option B', 'Option C'],
  },
  related: {
    settings: () => {
      const {
        label,
        placeholder,
        required,
        options,
        actions: { setProp },
      } = useNode((n) => ({
        label: n.data.props.label as string,
        placeholder: n.data.props.placeholder as string,
        required: n.data.props.required as boolean,
        options: (n.data.props.options ?? []) as string[],
      }));
      return (
        <div className="panel-section">
          <div className="panel-section-title">Liste déroulante</div>
          <Field label="Label">
            <input
              className="field-input"
              value={label}
              onChange={(e) =>
                setProp((p: FormFieldSelectProps) => {
                  p.label = e.target.value;
                })
              }
            />
          </Field>
          <Field label="Placeholder">
            <input
              className="field-input"
              value={placeholder}
              onChange={(e) =>
                setProp((p: FormFieldSelectProps) => {
                  p.placeholder = e.target.value;
                })
              }
            />
          </Field>
          <div className="toggle-row">
            <span className="toggle-label">Obligatoire</span>
            <label className="toggle">
              <input
                type="checkbox"
                checked={required}
                onChange={() =>
                  setProp((p: FormFieldSelectProps) => {
                    p.required = !p.required;
                  })
                }
              />
              <span className="toggle-slider" />
            </label>
          </div>
          <div style={{ marginTop: 10 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: 6,
              }}
            >
              Options
            </div>
            {options.map((opt, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 6,
                  alignItems: 'center',
                  marginBottom: 4,
                }}
              >
                <input
                  className="field-input"
                  style={{ flex: 1, fontSize: 12 }}
                  value={opt}
                  onChange={(e) =>
                    setProp((p: FormFieldSelectProps) => {
                      const next = [...(p.options ?? [])];
                      next[i] = e.target.value;
                      p.options = next;
                    })
                  }
                />
                <button
                  onClick={() =>
                    setProp((p: FormFieldSelectProps) => {
                      p.options = (p.options ?? []).filter((_, j) => j !== i);
                    })
                  }
                  style={{
                    width: 22,
                    height: 22,
                    border: 'none',
                    background: 'rgba(220,38,38,0.08)',
                    color: '#dc2626',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: 14,
                    flexShrink: 0,
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              className="btn-sm"
              onClick={() =>
                setProp((p: FormFieldSelectProps) => {
                  p.options = [
                    ...(p.options ?? []),
                    `Option ${(p.options ?? []).length + 1}`,
                  ];
                })
              }
              style={{ marginTop: 4, fontSize: 11 }}
            >
              + Ajouter une option
            </button>
          </div>
        </div>
      );
    },
  },
};

// ── 19. FORM CONTAINER ────────────────────────────────────
//
// A droppable canvas that groups FormField* blocks. Field state
// lives in the single shared PublishedFormContext mounted once
// per published page (see PublishedEventPage), so values persist
// across containers/tabs and are submitted together with the
// Recap & Validation order — this block only provides layout.

interface FormContainerProps {
  children?: React.ReactNode;
  title?: string;
  submitLabel?: string;
}

export const FormContainer = ({
  children,
  title = '',
}: FormContainerProps): React.ReactElement => {
  const {
    connectors: { connect, drag },
    hasChildren,
  } = useNode((n) => ({ hasChildren: n.data.nodes.length > 0 }));
  const { enabled: editorEnabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  return (
    <div
      ref={(ref) => connect(drag(ref as HTMLElement))}
      style={{
        border: editorEnabled ? '2px dashed #93c5fd' : '1px solid #e5e7eb',
        borderRadius: 8,
        background: '#fff',
        margin: '8px 0',
      }}
    >
      {editorEnabled && (
        <div
          style={{
            fontSize: 11,
            color: '#2563eb',
            fontWeight: 700,
            padding: '6px 16px',
            background: '#eff6ff',
            borderBottom: '1px solid #bfdbfe',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          📋 Conteneur formulaire
        </div>
      )}

      {title && (
        <h3
          style={{
            fontSize: 16,
            fontWeight: 700,
            margin: '16px 16px 4px',
            color: '#111827',
          }}
        >
          {title}
        </h3>
      )}

      {/* children renders Craft.js child nodes (form fields dropped here) */}
      <div
        style={{
          minHeight: editorEnabled ? 64 : undefined,
          padding: '4px 0',
        }}
      >
        {children}
        {editorEnabled && !hasChildren && (
          <div
            style={{
              padding: '20px 16px',
              textAlign: 'center',
              color: '#9ca3af',
              fontSize: 13,
              border: '1px dashed #d1d5db',
              margin: '8px 12px',
              borderRadius: 6,
              pointerEvents: 'none',
            }}
          >
            Déposez vos champs de formulaire ici
          </div>
        )}
      </div>
    </div>
  );
};

FormContainer.craft = {
  displayName: 'FormContainer',
  isCanvas: true,
  props: {
    title: '',
    submitLabel: 'Soumettre',
  },
  rules: {
    canMoveIn: () => true,
  },
  related: {
    settings: () => {
      const {
        title,
        submitLabel,
        actions: { setProp },
      } = useNode((n) => ({
        title: n.data.props.title as string,
        submitLabel: n.data.props.submitLabel as string,
      }));
      return (
        <div className="panel-section">
          <div className="panel-section-title">Conteneur formulaire</div>
          <Field label="Titre (optionnel)">
            <input
              className="field-input"
              value={title}
              placeholder="ex: Formulaire d'inscription"
              onChange={(e) =>
                setProp((p: FormContainerProps) => {
                  p.title = e.target.value;
                })
              }
            />
          </Field>
          <Field label="Label du bouton">
            <input
              className="field-input"
              value={submitLabel}
              onChange={(e) =>
                setProp((p: FormContainerProps) => {
                  p.submitLabel = e.target.value;
                })
              }
            />
          </Field>
        </div>
      );
    },
  },
};

// ── Resolver map (pass to <Editor resolver={...}>) ────────
export const RESOLVER = {
  Titre,
  Texte,
  Image,
  Bouton,
  Separateur,
  Espaceur,
  Section,
  Cta,
  Libre,
  Formulaire,
  Pricing,
  Offre,
  ValidationBlock,
  FormFieldInput,
  FormFieldTextarea,
  FormFieldRadio,
  FormFieldCheckbox,
  FormFieldSelect,
  FormContainer,
  DefaultAccueil,
  DefaultOffres,
  DefaultValidation,
  DefaultDynamicTab,
};
